/**
 * Machine-backed Remix flow controller (plain module, no react-sweet-state).
 *
 * This replaces the previous `remixMachineStore` (which mirrored machine phase
 * into a SweetState store). The machine is now the SINGLE source of truth:
 *
 * - Each remix flow is a state **machine** (`./remixMachine`). Machines are
 *   imperative, non-serialisable objects; they live in a module-level registry
 *   (`machineRegistry`), NOT in any React store.
 * - React consumers read the machine directly via the `useSyncExternalStore`
 *   hooks in `./useMachineState` (phase, instance view, active ids, isAnyRemixing),
 *   which subscribe through `./machineBridge`. There is no mirrored phase copy.
 * - This file owns only the **write/lifecycle** side: create a flow, wire its
 *   transitions/teardown to the bridge, send events, end/reset. All of this is
 *   plain functions callable from anywhere (React or not) — the registry and
 *   bridge are module-scoped.
 *
 * The explicit-machine model (events in, `onTransition`/`onDone` out, effects for
 * side-effects) is exactly what the reviewers asked for; dropping SweetState
 * removes the redundant second source of truth.
 */

import { buildRemixCommandEffects } from './commands/commandEffects';
import {
	defaultRemixCommandDispatcher,
	type RemixCommandMap,
	type RemixCommandDispatcher,
	type RemixExplicitFlowEndReason,
	type RemixFlowEndReason,
} from './commands/remixCommandBus';
import { buildRemixEffects } from './effects/remixEffects';
import type { RemixEffectPorts } from './effects/types';
import { registerMachineWithBridge, unregisterMachineFromBridge } from './machineBridge';
import { isActivePhase, type RemixMetadata, type RemixPhase } from './remix-instance-types';
import {
	createRemixCreateMachine,
	createRemixEditMachine,
	type RemixMachine,
	type RemixMachineContext,
	type RemixEffectHooks,
} from './remixMachine';

/**
 * Single-active policy, same as before: a removable guard on top of a plural
 * model. Flip to `false` (or make per-call) to allow concurrent flows.
 */
const SINGLE_ACTIVE_POLICY = true;

type RemixFlowCommandBase = Omit<RemixCommandMap['RemixFlowEnded'], 'reason'>;

interface RemixRegistryRecord {
	machine: RemixMachine;
	dispatcher?: RemixCommandDispatcher;
	commandBase: RemixFlowCommandBase;
}

/**
 * Non-reactive registry of live flows, keyed by remix id. Alongside each
 * machine, retain its dispatcher and immutable command identity so teardown
 * never depends on mutable machine context.
 */
const machineRegistry = new Map<string, RemixRegistryRecord>();

/**
 * Read a currently registered machine by id (for sending events / reading state
 * and context). The returned raw reference is invalid after teardown and must
 * not be retained across the flow lifecycle.
 */
export const getRemixMachine = (id: string): RemixMachine | undefined =>
	machineRegistry.get(id)?.machine;

/** The ids of all currently-registered machines. */
export const listRemixMachineIds = (): string[] => Array.from(machineRegistry.keys());

/**
 * Non-hook read of "is any flow active" (non-terminal), from the registry.
 * Used by the single-active guard below and re-exported for imperative callers.
 */
export const isAnyRemixActive = (): boolean => {
	for (const { machine } of machineRegistry.values()) {
		if (isActivePhase(machine.state() as RemixPhase)) {
			return true;
		}
	}
	return false;
};

const generateRemixId = (): string => `remix-${crypto.randomUUID()}`;
const generateConversationId = (): string => crypto.randomUUID();

/**
 * Remove a flow from every synchronous read path, announce its final teardown,
 * then dispose the machine after the current state-machine send has unwound.
 */
const teardownRemix = (id: string, reason: RemixFlowEndReason): void => {
	const record = machineRegistry.get(id);
	if (!record) {
		// Preserve stale bridge cleanup for unknown/already-ended ids.
		unregisterMachineFromBridge(id);
		return;
	}

	// Delete before dispatch: a command handler may re-enter endRemix synchronously.
	machineRegistry.delete(id);
	unregisterMachineFromBridge(id);

	try {
		record.dispatcher?.dispatch({
			type: 'RemixFlowEnded',
			...record.commandBase,
			reason,
		});
	} finally {
		// Command handlers run inside machine effects. Destroying synchronously here
		// can therefore corrupt an in-flight state-machine send.
		queueMicrotask(() => record.machine.destroy());
	}
};

interface StartRemixBaseArgs {
	metadata: RemixMetadata;
	/** Provided for edits; generated for creates. */
	conversationId?: string;
	/** Durable cancellation session reserved for renderer-owned observation. */
	cancellationSessionId?: string;
	/** Cross-store ports for this surface (editor loading, chat). */
	ports?: RemixEffectPorts;
	/**
	 * Opt in to the command model (Abhi): the machine's enter/exit effects
	 * dispatch typed commands (RemixStarted/GenerationEnded/FlowEnded/...) through
	 * this dispatcher, in addition to any `ports`. Pass a bridge-backed dispatcher
	 * for real (incl. cross-frame) delivery, or omit and set `useCommandBus` to
	 * use the process-default in-memory dispatcher.
	 */
	commandDispatcher?: RemixCommandDispatcher;
	/** Use the process-default in-memory dispatcher when no explicit one is given. */
	useCommandBus?: boolean;
}

/**
 * Start arguments, discriminated on `kind` so the compiler enforces what each
 * flow actually requires:
 *
 * - **create** — `targetNodeId` is optional. An *anchored* create (remixing a
 *   selected node) has one and uses it to target inline/embed loading; an
 *   unanchored create has nowhere to point yet.
 * - **update** — both `targetNodeId` and `updateSentinel` are required. If we
 *   are editing, by definition we know which node we are editing and which
 *   result is already applied.
 */
export type StartRemixArgs =
	| (StartRemixBaseArgs & {
			kind: 'create';
			/** Optional anchor node (mediaId / contentMauiId) for loading UI. */
			targetNodeId?: string;
	  })
	| (StartRemixBaseArgs & {
			kind: 'update';
			/** Node identity of the already-inserted remix being edited. */
			targetNodeId: string;
			/**
			 * Invocation id of the currently-applied result, used to suppress it
			 * until a newer generation arrives. See `getRemixResultPayload`.
			 */
			updateSentinel: string;
			/** Number of conversation messages observed before this update was sent. */
			messageCountBeforeSend?: number;
	  });

export interface StartRemixResult {
	remixId: string;
	conversationId: string;
}

/**
 * Start a new remix flow: create the machine, register it, wire its transitions
 * to the bridge, fire initial effects, and add it to the registry. Returns
 * `undefined` if blocked by the single-active policy.
 */
export const startRemix = (args: StartRemixArgs): StartRemixResult | undefined => {
	if (SINGLE_ACTIVE_POLICY && isAnyRemixActive()) {
		return undefined;
	}

	const remixId = generateRemixId();
	const conversationId = args.conversationId ?? generateConversationId();
	const startedAt = Date.now();

	// Resolve the command dispatcher: an explicit (e.g. bridge-backed) one, or
	// the process-default in-memory dispatcher when `useCommandBus` is set, or none.
	const dispatcher: RemixCommandDispatcher | undefined =
		args.commandDispatcher ?? (args.useCommandBus ? defaultRemixCommandDispatcher : undefined);

	// Two independent effect sources: port-based effects (direct calls) and, when
	// a dispatcher is present, command effects (dispatch typed commands). They are
	// NOT merged — the machine registers each set's callbacks separately, because
	// the builder's `.on('enter'|'exit', cb)` accumulates rather than overwrites.
	// Order matters: ports run before commands.
	//
	// The two are complementary, not alternatives: `ports` is the LOCAL writer of
	// editor/chat state, while commands are announcements for any number of
	// observers (analytics, other frames). Do not also register
	// `createPortsCommandHandler` with these same ports — that would make the
	// command path a second writer and double-fire every effect.
	const effectSets: RemixEffectHooks[] = [buildRemixEffects(args.ports ?? {})];
	if (dispatcher) {
		effectSets.push(buildRemixCommandEffects(dispatcher));
	}

	// `targetNodeId` is part of the base context, not an edit-only field: an
	// anchored create carries one too (it is what inline/embed loading targets).
	// Left `undefined` — never coerced to '' — so "unanchored" stays honest and
	// the loading port can distinguish "no anchor" from "an anchor called ''".
	const baseContext = {
		remixId,
		kind: args.kind,
		conversationId,
		cancellationSessionId: args.cancellationSessionId,
		metadata: args.metadata,
		startedAt,
		targetNodeId: args.targetNodeId,
	} satisfies Partial<RemixMachineContext>;

	const machine =
		args.kind === 'update'
			? createRemixEditMachine(
					{
						...baseContext,
						updateSentinel: args.updateSentinel,
						messageCountBeforeSend: args.messageCountBeforeSend,
						targetNodeId: args.targetNodeId,
					},
					...effectSets,
				)
			: createRemixCreateMachine(baseContext, ...effectSets);

	// `INSERTED` is the machine's only transition to `done`. Final transitions bypass
	// `onTransition` and synchronously fire `onDone` inside `send()`, making this the
	// required automatic-teardown hook. `teardownRemix` removes and announces the flow
	// immediately, then defers destruction until the in-flight `send()` has unwound.
	machine.onDone(() => {
		teardownRemix(remixId, 'inserted');
	});

	machineRegistry.set(remixId, {
		machine,
		dispatcher,
		commandBase: {
			remixId,
			conversationId,
			targetNodeId: args.targetNodeId,
		},
	});

	// Bridge the machine to React so `useSyncExternalStore` consumers re-render on
	// transitions and can read the current phase on mount. Registering also
	// notifies aggregate subscribers that membership changed (e.g. isAnyRemixing).
	registerMachineWithBridge(remixId, machine);

	// @atlassian/state-machine does NOT fire the initial state's `onEnter` effect
	// at construction (effects only run on transitions). A flow starts life in
	// `thinking`, so invoke every source's "entered thinking" effect explicitly
	// here. Subsequent enter/exit of `thinking` are handled by the machine.
	//
	// This deliberately calls the SAME callbacks the machine registered rather
	// than re-implementing their bodies per source — otherwise adding a third
	// effect source would silently miss its initial fire.
	//
	// The context is read back off the machine so it is exactly what the effects
	// would receive on a real transition. The event producer is a no-op: there is
	// no triggering event at construction, and sending into a machine mid-setup
	// is not safe.
	const initialContext = machine.context();
	const noopEventProducer = (): void => {};
	for (const set of effectSets) {
		set.onEnterThinking?.(initialContext, noopEventProducer);
	}

	return { remixId, conversationId };
};

/**
 * Send an event to a flow's machine. No-op if the flow does not exist. This is
 * the write path that drives phase transitions; consumers previously reached for
 * `getRemixMachine(id)?.send(...)` directly — this wrapper keeps the call site
 * intention-revealing and central. The event type is the machine's own `send`
 * parameter (the `EventLookup`-resolved event object).
 */
export const sendRemixEvent = (id: string, event: Parameters<RemixMachine['send']>[0]): void => {
	machineRegistry.get(id)?.machine.send(event);
};

/**
 * Explicitly end a flow after abort or cancellation.
 *
 * Caller precondition: abort any in-flight chat request, then send the matching
 * terminal machine event before calling this. Abort first: once the flow is
 * removed, its watcher can no longer observe the abort. This function does not
 * synthesize `RemixGenerationEnded`; it only emits the final `RemixFlowEnded`
 * announcement.
 */
export const endRemix = (id: string, reason: RemixExplicitFlowEndReason): void =>
	teardownRemix(id, reason);

/** Reset everything (tests). Destroys all machines and clears the registry. */
export const resetRemixFlows = (): void => {
	// Test-only hard reset deliberately bypasses command dispatch and deferred
	// disposal so each test starts from a fully clean synchronous state.
	machineRegistry.forEach(({ machine }, id) => {
		machine.destroy();
		unregisterMachineFromBridge(id);
	});
	machineRegistry.clear();
};
